/**
 * VK Sticky Cart Bar - v5
 * =======================
 * - Product links in drawer
 * - Variant info display
 * - VK Green branding
 */

(function() {
  'use strict';

  if (window.__VK_STICKY_CART_BOUND) return;
  window.__VK_STICKY_CART_BOUND = true;

  const CART_URL = '/cart.js';
  const HIDE_PATHS = ['/cart', '/checkout', '/checkouts'];
  const STORAGE_KEY = 'vk-sticky-cart-collapsed';

  let root = null;
  let barEl = null;
  let drawerEl = null;
  let drawerItemsEl = null;
  let drawerCountEl = null;
  let countEl = null;
  let totalEl = null;
  let previewEl = null;
  let collapseBtn = null;
  let drawerCloseBtn = null;
  let fabEl = null;
  let fabCountEl = null;

  let isCollapsed = false;
  let isExpanded = false;

  function formatMoney(cents) {
    if (typeof cents !== 'number' || !Number.isFinite(cents)) cents = 0;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { 
        const format = window.theme?.moneyFormat || window.Shopify?.money_format || '£{{amount_with_comma_separator}}';
        return window.Shopify.formatMoney(cents, format); 
      } catch (e) {}
    }
    const amount = (cents / 100).toFixed(2);
    const parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '£' + parts.join('.');
  }

  function truncate(str, len) {
    if (!str) return '';
    str = String(str);
    return str.length <= len ? str : str.slice(0, len - 1) + '…';
  }

  function shouldHide() {
    const path = window.location.pathname.toLowerCase();
    for (const p of HIDE_PATHS) {
      if (path === p || path.startsWith(p + '/')) return true;
    }
    return path.includes('/checkouts/');
  }

  function updateVisibility() {
    if (!root) return;
    if (shouldHide()) {
      root.setAttribute('data-hidden', 'true');
    } else {
      root.removeAttribute('data-hidden');
    }
  }

  function setCollapsed(collapsed) {
    isCollapsed = !!collapsed;
    if (!root) return;
    
    if (isCollapsed) {
      root.classList.add('is-collapsed');
      root.classList.remove('is-expanded');
      isExpanded = false;
    } else {
      root.classList.remove('is-collapsed');
    }
    
    try { sessionStorage.setItem(STORAGE_KEY, isCollapsed ? '1' : '0'); } catch (e) {}
  }

  function restoreCollapsedState() {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1') setCollapsed(true);
    } catch (e) {}
  }

  function setExpanded(expanded) {
    isExpanded = !!expanded;
    if (!root) return;
    
    if (isExpanded) {
      root.classList.add('is-expanded');
      root.classList.remove('is-collapsed');
      isCollapsed = false;
    } else {
      root.classList.remove('is-expanded');
    }
  }

  function buildPreviewHTML(items) {
    if (!items || items.length === 0) {
      return '<span class="vk-sticky-cart__preview-empty">Your basket is empty</span>';
    }
    let html = '';
    const preview = items.slice(0, 2);
    for (const item of preview) {
      const title = truncate(item.product_title || item.title || 'Item', 18);
      html += `<span class="vk-sticky-cart__preview-item">${item.quantity}× ${title}</span>`;
    }
    if (items.length > 2) {
      html += `<span class="vk-sticky-cart__preview-more" data-vk-sticky-cart-expand>+${items.length - 2} more ▲</span>`;
    }
    return html;
  }

  function buildDrawerItemsHTML(items) {
    if (!items || items.length === 0) {
      return '<div class="vk-sticky-cart__drawer-empty">Your basket is empty</div>';
    }
    let html = '';
    for (const item of items) {
      const title = truncate(item.product_title || item.title || 'Item', 30);
      const price = formatMoney(item.final_line_price || item.line_price || 0);
      const url = item.url || '#';
      
      // Get variant title (e.g., "6" or "12" for serves)
      let variantDisplay = '';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variantDisplay = `<span class="vk-sticky-cart__drawer-item-variant">(${item.variant_title})</span>`;
      }
      
      html += `
        <a href="${url}" class="vk-sticky-cart__drawer-item">
          <span class="vk-sticky-cart__drawer-item-qty">${item.quantity}×</span>
          <span class="vk-sticky-cart__drawer-item-title">${title} ${variantDisplay}</span>
          <span class="vk-sticky-cart__drawer-item-price">${price}</span>
        </a>
      `;
    }
    return html;
  }

  function updateUI(cart) {
    if (!cart || !root) return;

    const count = cart.item_count || 0;
    const total = cart.total_price || 0;
    const items = cart.items || [];

    if (countEl) {
      countEl.textContent = String(count);
      count < 1 ? countEl.setAttribute('hidden', '') : countEl.removeAttribute('hidden');
    }
    if (fabCountEl) {
      fabCountEl.textContent = String(count);
    }
    if (drawerCountEl) {
      drawerCountEl.textContent = String(count);
    }

    if (totalEl) {
      totalEl.textContent = formatMoney(total);
    }

    if (previewEl) {
      previewEl.innerHTML = buildPreviewHTML(items);
      const expandTrigger = previewEl.querySelector('[data-vk-sticky-cart-expand]');
      if (expandTrigger) {
        expandTrigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          setExpanded(!isExpanded);
        });
      }
    }

    if (drawerItemsEl) {
      drawerItemsEl.innerHTML = buildDrawerItemsHTML(items);
    }

    root.setAttribute('data-count', String(count));
    root.setAttribute('data-empty', count < 1 ? 'true' : 'false');
  }

  function fetchCart() {
    return fetch(CART_URL, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(r => r.ok ? r.json() : Promise.reject('Cart fetch failed'));
  }

  function refresh() {
    fetchCart().then(updateUI).catch(err => console.warn('[vk-sticky-cart]', err));
  }

  function patchFetch() {
    const originalFetch = window.fetch;
    window.fetch = function(url, options) {
      const urlStr = String(url);
      const isCartOperation = 
        urlStr.includes('/cart/add') || 
        urlStr.includes('/cart/change') || 
        urlStr.includes('/cart/update') ||
        urlStr.includes('/cart/clear');

      if (isCartOperation) {
        return originalFetch.apply(this, arguments).then(response => {
          setTimeout(refresh, 50);
          return response;
        });
      }
      return originalFetch.apply(this, arguments);
    };
  }

  function patchXHR() {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url) {
      this._vkUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function() {
      const url = this._vkUrl || '';
      const isCartOperation = 
        url.includes('/cart/add') || 
        url.includes('/cart/change') || 
        url.includes('/cart/update') ||
        url.includes('/cart/clear');

      if (isCartOperation) {
        this.addEventListener('load', () => setTimeout(refresh, 50));
      }
      return originalSend.apply(this, arguments);
    };
  }

  function bindEvents() {
    const handler = () => refresh();
    window.addEventListener('vk:cart:updated', handler);
    document.addEventListener('vk:cart:updated', handler);
    document.addEventListener('cart:refresh', handler);
    document.addEventListener('cart:updated', handler);

    if (collapseBtn) {
      collapseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        setCollapsed(true);
      });
    }

    if (fabEl) {
      fabEl.addEventListener('click', (e) => {
        e.preventDefault();
        setCollapsed(false);
      });
    }

    if (drawerCloseBtn) {
      drawerCloseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        setExpanded(false);
      });
    }

    if (barEl) {
      barEl.addEventListener('click', (e) => {
        if (e.target.closest('a, button, [data-vk-sticky-cart-expand]')) return;
        if (!root || root.getAttribute('data-empty') === 'true') return;
        window.location.href = '/cart';
      });
    }

    document.addEventListener('click', (e) => {
      if (!isExpanded) return;
      if (!root.contains(e.target)) {
        setExpanded(false);
      }
    });

    window.addEventListener('popstate', updateVisibility);

    new MutationObserver(mutations => {
      for (const m of mutations) {
        if (m.attributeName === 'class' || m.attributeName === 'data-template') {
          updateVisibility();
        }
      }
    }).observe(document.body, { attributes: true, attributeFilter: ['class', 'data-template'] });
  }

  function init() {
    root = document.querySelector('[data-vk-sticky-cart]');
    if (!root) return;

    barEl = root.querySelector('[data-vk-sticky-cart-bar]');
    drawerEl = root.querySelector('[data-vk-sticky-cart-drawer]');
    drawerItemsEl = root.querySelector('[data-vk-sticky-cart-drawer-items]');
    drawerCountEl = root.querySelector('[data-vk-sticky-cart-drawer-count]');
    countEl = root.querySelector('[data-vk-sticky-cart-count]');
    totalEl = root.querySelector('[data-vk-sticky-cart-total]');
    previewEl = root.querySelector('[data-vk-sticky-cart-preview]');
    collapseBtn = root.querySelector('[data-vk-sticky-cart-collapse]');
    drawerCloseBtn = root.querySelector('[data-vk-sticky-cart-drawer-close]');
    fabEl = root.querySelector('[data-vk-sticky-cart-fab]');
    fabCountEl = root.querySelector('[data-vk-sticky-cart-fab-count]');

    updateVisibility();
    restoreCollapsedState();
    
    patchFetch();
    patchXHR();
    
    bindEvents();
    refresh();
    
    root.classList.add('is-ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();